import React, { useState } from 'react';
import { X, CreditCard, Shield, Check, Star } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { useAuth } from '../contexts/AuthContext';
import { getFreshToken } from '../lib/firebase';
import axios from 'axios';

const PaymentModal = ({ isOpen, onClose, selectedPlan, onSuccess }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handlePayment = async () => {
    setLoading(true);
    setError('');

    try {
      const token = await getFreshToken();
      if (!token) {
        setError('Please sign in to continue.');
        setLoading(false);
        return;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/stripe/create-checkout-session`,
        {
          price_id: selectedPlan.stripePriceId,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          }
        }
      );

      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        setError('Failed to create payment session. Please try again.');
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.response?.data?.detail || 'Payment failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-2xl font-bold text-gray-900">Complete Your Purchase</h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-8 w-8 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Selected Plan Summary */}
        <div className="p-6 border-b bg-gradient-to-r from-blue-50 to-purple-50">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                {selectedPlan.name}
                {selectedPlan.badge && (
                  <Badge className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white">
                    {selectedPlan.badge}
                  </Badge>
                )}
              </h3>
              <p className="text-sm text-gray-600">{selectedPlan.description}</p>
            </div>
          </div>
          
          <div className="flex items-center justify-between text-2xl font-bold text-gray-900">
            <span>Total:</span>
            <span className="text-amber-600">
              {selectedPlan.priceDisplay}
              <span className="text-sm text-gray-500">
                {selectedPlan.priceSuffix}
              </span>
            </span>
          </div>
        </div>

        {/* Features Preview */}
        <div className="p-6 border-b">
          <h4 className="font-semibold text-gray-900 mb-3">What's included:</h4>
          <div className="space-y-2">
            {selectedPlan.features.slice(0, 4).map((feature, index) => (
              <div key={index} className="flex items-center gap-2">
                <Check className="h-4 w-4 text-amber-500" />
                <span className="text-sm text-gray-700">{feature}</span>
              </div>
            ))}
            {selectedPlan.features.length > 4 && (
              <div className="text-sm text-gray-500 pl-6">
                +{selectedPlan.features.length - 4} more features
              </div>
            )}
          </div>
        </div>

        {/* Security & Trust */}
        <div className="p-6 border-b bg-gray-50">
          <div className="flex items-center gap-3 mb-3">
            <Shield className="h-5 w-5 text-amber-500" />
            <span className="text-sm font-medium text-gray-900">Secure Payment</span>
          </div>
          <p className="text-xs text-gray-600 mb-3">
            Your payment information is encrypted and secure. Powered by Stripe.
          </p>
          <div className="flex items-center gap-4 text-xs text-gray-500">
            <span>✓ 256-bit SSL encryption</span>
            <span>✓ Cancel anytime</span>
            <span>✓ Founding rate locked in forever</span>
          </div>
        </div>

        {/* Payment Button */}
        <div className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
          
          <Button
            onClick={handlePayment}
            disabled={loading}
            className="w-full bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-black font-bold py-3 px-6 rounded-xl transition-all duration-300 transform hover:scale-105"
          >
            {loading ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Processing...
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Pay {selectedPlan.priceDisplay} Now
              </div>
            )}
          </Button>
          
          <p className="text-xs text-gray-500 text-center mt-3">
            By completing this purchase, you agree to our Terms of Service and Privacy Policy.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;